import { Alert, Box, Grid, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import RenrolmentAccordian from '../RenrolmentAccordian/RenrolmentAccordian';
import UploadButton from '../UploadButton/UploadButton';
import BasicDatePicker from '../DatePicker/DatePicker';
import Paragraph from '../Paragraph/Paragraph';
import { currentYear as getCurrentYear } from '../../utils/years';

const HCCDiscountForm = ({
  showHCCfields,
  expiryMessage = null,
  minExpiry,
  enrolledchildrens,
  college,
  cardAlreadySent = false,
  isGhostLogin,
  UploadedHCC,
  UploadMessageFunc,
  EnableNextBtn,
  ParentID,
  setHCCExpiryDatefunc,
  ExpiryError,
  formik,
  hasSubmitted, // <-- add this prop
  storedUrls = [],
  onRemoveStored,
}) => {

  const currentYear = getCurrentYear();

  // 🔴 Step-level error flags
  const step1HasError = !!formik.errors.hccSelections;
  const step2HasError = !!formik.errors.hccImages;
  const step3HasError = !!formik.errors.hccExpiry;

  // 🔁 Refs for scrolling
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  const childRefs = useRef([]);

  // Initialize refs for each child
  useEffect(() => {
    childRefs.current = enrolledchildrens.map((_, i) => childRefs.current[i] ?? React.createRef());
  }, [enrolledchildrens]);

  // 🔽 Scroll to first error when any error appears
  useEffect(() => {

    // if (formik.isValidating) {
    if (!hasSubmitted) return;

    const hccErrors = formik.errors.hccSelections;
    if (Array.isArray(hccErrors)) {
      step1Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    else if (step2HasError && step2Ref.current) {
      step2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    else if (step3HasError && step3Ref.current) {
      step3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // }
  }, [formik.isValidating]);


  return (
    <form>
      <Grid container spacing={2} mt={1}>

        {/* STEP 1 */}
        <Grid item xs={12} ref={step1Ref}>
          <Typography component="h6" sx={{ color: '#242424', fontSize: '14px' }}>
            {'Step 1: Please select the child who is eligible for the Health Care Card Discount by selecting "Yes" or "No"'}
          </Typography>
          <RenrolmentAccordian
            showHCCfields={showHCCfields}
            enrolledchildrens={enrolledchildrens}
            formik={formik}
            childRefs={childRefs}
          />
        </Grid>

        {/* STEP 2 */}
        <Grid
          item
          xs={12}
          ref={step2Ref}
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "10px",
          }}
        >
          <Typography
            component="h6"
            sx={{ color: "#242424", fontSize: "14px", mb: 1.5 }}
          >
            {"Step 2: Please Upload your Health Care Card Front and Back Pictures"}
          </Typography>

          <Box sx={{ width: '100%' }}>
            {((storedUrls || []).filter(Boolean).length === 2 || cardAlreadySent) && (
              <Grid item lg={6} xs={12} sx={{ mb: 1 }}>
                <Paragraph
                  color="#008000"
                  text={cardAlreadySent && (storedUrls || []).filter(Boolean).length < 2
                    ? 'Your Health Care Card is already on file. You can continue without uploading it again.'
                    : 'Files Uploaded Successfully'}
                />
              </Grid>
            )}
            {/* The boxes stay on screen. Replacing them with this message, once a previous
                attempt had uploaded the card, left nothing to attach when the pictures could
                not be shown — and the next step then refused to open. */}
            <UploadButton
              isGhostLogin={isGhostLogin}
              college={college}
              UploadedHCC={UploadedHCC}
              UploadMessageFunc={UploadMessageFunc}
              EnableNextBtn={EnableNextBtn}
              ParentID={ParentID}
              formik={formik}
              storedUrls={storedUrls}
              onRemoveStored={onRemoveStored}
            />
          </Box>
        </Grid>

        {/* STEP 3 */}
        <Grid item xs={12} ref={step3Ref} sx={{ marginTop: '20px', }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, // column on mobile, row on bigger screens
              gap: 1, // optional spacing
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="h6"
                sx={{ color: '#242424', margin: 0, fontSize: '14px' }}
              >
                Step 3: Please Select the Expiry Date
              </Typography>
            </Box>

            <Box>
              <BasicDatePicker
                value={formik.values.hccExpiry || null}
                minDate={minExpiry}
                setHCCExpiryDate={(date) => {
                  formik.setFieldValue('hccExpiry', date);
                  setHCCExpiryDatefunc(date);
                  setTimeout(() => {
                    formik.validateField('hccExpiry');
                  }, 50);
                }}
                error={Boolean(formik.touched.hccExpiry && formik.errors.hccExpiry)}
                helperText={formik.touched.hccExpiry && formik.errors.hccExpiry}
              />
            </Box>
          </Box>

          {/* An expired card stops the parent; one that runs out before the deadline only warns
              them. It used to stop them either way, which left a parent with a card expiring in
              October unable to finish the form at all. */}
          {expiryMessage && (
            <Alert
              severity={expiryMessage.severity}
              sx={{
                mt: 1.5,
                borderRadius: '12px',
                fontSize: '13px',
                alignItems: 'flex-start',
                ...(expiryMessage.severity === 'warning' && {
                  bgcolor: '#FFFBEB',
                  border: '1px solid #FCD34D',
                  color: '#92400E',
                  '& .MuiAlert-icon': { color: '#D97706' },
                }),
              }}
            >
              {expiryMessage.text}
            </Alert>
          )}
        </Grid>

      </Grid>
    </form >
  );
};

export default HCCDiscountForm;
